// html-to-image does not reliably re-apply CSS-module classes to fill/stroke on
// SVG elements when it clones the DOM, so unstyled shapes fall back to the SVG
// default (fill: black). Resolving and inlining these properties on the real
// elements right before capture guarantees the raster output matches what is on screen.
const SVG_PRESENTATION_PROPERTIES = [
  "fill",
  "fill-opacity",
  "stroke",
  "stroke-width",
  "stroke-dasharray",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-opacity",
  "opacity",
] as const;

/** Inlines the computed SVG styles below root and returns the function restoring the original ones. */
export const inlineComputedSvgStyles = (root: HTMLElement): (() => void) => {
  // Every style read first, then every write: alternating them per element made each read after a
  // write force a style recalculation. The reads are the same either way, as an element's inlined
  // values are the ones it already computes, and so the ones its descendants inherit.
  const svgElements = Array.from(root.querySelectorAll("svg, svg *"))
    .filter((element): element is SVGElement => element instanceof SVGElement)
    .map((element) => {
      const computedStyle = window.getComputedStyle(element);

      return {
        element,
        previousStyle: element.getAttribute("style"),
        values: SVG_PRESENTATION_PROPERTIES.map(
          (property) => [property, computedStyle.getPropertyValue(property)] as const,
        ),
      };
    });

  for (const { element, values } of svgElements) {
    for (const [property, value] of values) {
      if (value) {
        element.style.setProperty(property, value);
      }
    }
  }

  return () => {
    for (const { element, previousStyle } of svgElements) {
      if (previousStyle === null) {
        element.removeAttribute("style");
      } else {
        element.setAttribute("style", previousStyle);
      }
    }
  };
};
